'use client';

import { c as _c } from "react/compiler-runtime";
import { css, cx } from '@repo/panda/css';
import { type ToastVariantProps, toast } from '@repo/panda/recipes';
import * as React from 'react';
import { Button } from '../button';
import { Icon } from '../icon';
const ToastContext = React.createContext<ReturnType<typeof toast> | null>(null);
const useToastStyles = part => {
  const styles = React.useContext(ToastContext);
  if (!styles) {
    throw new Error(`\`${part}\` must be rendered inside \`Toast\`.`);
  }
  return styles;
};
type ToastDismissProps = {
  dismissable: true;
  onDismiss: () => void;
  /** Accessible name of the close button. Defaults to "Dismiss". */
  dismissLabel?: string;
} | {
  dismissable?: false;
  onDismiss?: never;
  dismissLabel?: never;
};
export type ToastProps = React.HTMLAttributes<HTMLDivElement> & Omit<ToastVariantProps, 'dismissable'> & ToastDismissProps & {
  /**
   * Rendered as given, so the caller picks the glyph and its color. A ds `Icon` sizes itself to
   * the toast's size step.
   */
  icon?: React.ReactNode;
  children: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
};
export type ToastPartProps = React.HTMLAttributes<HTMLDivElement> & {
  ref?: React.Ref<HTMLDivElement>;
};

/** Whether an event target is inside a `Toast`, e.g. so a modal can ignore clicks on toasts above it. */
export const isWithinToast = (target: EventTarget | null): boolean => target instanceof Element && target.closest('[data-toast]') !== null;
export const Toast = t0 => {
  const $ = _c(44);
  let children;
  let className;
  let icon;
  let onDismiss;
  let ref;
  let rest;
  let size;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      size,
      dismissable: t1,
      onDismiss,
      dismissLabel: t2,
      icon,
      role: t3,
      className,
      children,
      ref,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = icon;
    $[4] = onDismiss;
    $[5] = ref;
    $[6] = rest;
    $[7] = size;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    children = $[1];
    className = $[2];
    icon = $[3];
    onDismiss = $[4];
    ref = $[5];
    rest = $[6];
    size = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
  }
  const dismissable = t1 === undefined ? false : t1;
  const dismissLabel = t2 === undefined ? "Dismiss" : t2;
  const role = t3 === undefined ? "status" : t3;
  let styles;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[11] !== className || $[12] !== dismissable || $[13] !== ref || $[14] !== role || $[15] !== size) {
    styles = toast({
      size,
      dismissable
    });
    t4 = ref;
    t5 = role;
    t6 = "";
    t7 = cx(styles.root, className);
    $[11] = className;
    $[12] = dismissable;
    $[13] = ref;
    $[14] = role;
    $[15] = size;
    $[16] = styles;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    styles = $[16];
    t4 = $[17];
    t5 = $[18];
    t6 = $[19];
    t7 = $[20];
  }
  let t8;
  if ($[21] !== icon || $[22] !== styles.icon) {
    t8 = icon ? <span className={styles.icon}>{icon}</span> : null;
    $[21] = icon;
    $[22] = styles.icon;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== children || $[25] !== styles.content) {
    t9 = <div className={styles.content}>{children}</div>;
    $[24] = children;
    $[25] = styles.content;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== styles || $[28] !== t9) {
    t10 = <ToastContext.Provider value={styles}>{t9}</ToastContext.Provider>;
    $[27] = styles;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== dismissLabel || $[31] !== dismissable || $[32] !== onDismiss || $[33] !== styles.dismiss) {
    t11 = dismissable && <Button variant="ghost" size="xs" aria-label={dismissLabel} onClick={onDismiss} className={cx(styles.dismiss, css({
      paddingX: "0"
    }))}><Icon icon="close" decorative={true} /></Button>;
    $[30] = dismissLabel;
    $[31] = dismissable;
    $[32] = onDismiss;
    $[33] = styles.dismiss;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== rest || $[36] !== t10 || $[37] !== t11 || $[38] !== t4 || $[39] !== t5 || $[40] !== t6 || $[41] !== t7 || $[42] !== t8) {
    t12 = <div ref={t4} role={t5} data-toast={t6} className={t7} {...rest}>{t8}{t10}{t11}</div>;
    $[35] = rest;
    $[36] = t10;
    $[37] = t11;
    $[38] = t4;
    $[39] = t5;
    $[40] = t6;
    $[41] = t7;
    $[42] = t8;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  return t12;
};
export const ToastHeading = t0 => {
  const $ = _c(11);
  let className;
  let ref;
  let rest;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = ref;
    $[3] = rest;
  } else {
    className = $[1];
    ref = $[2];
    rest = $[3];
  }
  const styles = useToastStyles("ToastHeading");
  let t1;
  if ($[4] !== className || $[5] !== styles.heading) {
    t1 = cx(styles.heading, className);
    $[4] = className;
    $[5] = styles.heading;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== ref || $[8] !== rest || $[9] !== t1) {
    t2 = <div ref={ref} className={t1} {...rest} />;
    $[7] = ref;
    $[8] = rest;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
};
export const ToastBody = t0 => {
  const $ = _c(11);
  let className;
  let ref;
  let rest;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = ref;
    $[3] = rest;
  } else {
    className = $[1];
    ref = $[2];
    rest = $[3];
  }
  const styles = useToastStyles("ToastBody");
  let t1;
  if ($[4] !== className || $[5] !== styles.body) {
    t1 = cx(styles.body, className);
    $[4] = className;
    $[5] = styles.body;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== ref || $[8] !== rest || $[9] !== t1) {
    t2 = <div ref={ref} className={t1} {...rest} />;
    $[7] = ref;
    $[8] = rest;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
};
export const ToastActions = t0 => {
  const $ = _c(11);
  let className;
  let ref;
  let rest;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = ref;
    $[3] = rest;
  } else {
    className = $[1];
    ref = $[2];
    rest = $[3];
  }
  const styles = useToastStyles("ToastActions");
  let t1;
  if ($[4] !== className || $[5] !== styles.actions) {
    t1 = cx(styles.actions, className);
    $[4] = className;
    $[5] = styles.actions;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== ref || $[8] !== rest || $[9] !== t1) {
    t2 = <div ref={ref} className={t1} {...rest} />;
    $[7] = ref;
    $[8] = rest;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
};